import { useFeatureGate } from "@statsig/react-bindings";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import Config from "@app/config";

import PushToCrm from "./PushToCrm";
import ShareToSlack from "./ShareToSlack";
import type { ShareProviderConfig, ShareProviderProps } from "./types";

/**
 * Types
 */
interface UseExternalShareProviderArgs {
  variant: "menu" | "dialog";
}

/**
 * Constants
 */
const MAX_NAV_PROVIDERS = 4;

const SHARE_PROVIDERS: ShareProviderConfig[] = [
  {
    providerId: "slack_bot",
    pushIntegrationKind: "slack_bot",
    Component: ShareToSlack,
  },
  {
    providerId: "hubspot",
    pushIntegrationKind: "hubspot_push",
    Component: PushToCrm,
  },
];

const SHARE_PROVIDERS_WITH_SALESFORCE: ShareProviderConfig[] = [
  ...SHARE_PROVIDERS,
  {
    providerId: "salesforce",
    pushIntegrationKind: "salesforce_push",
    Component: PushToCrm,
  },
];

const sortAndPartitionOptions = (
  variant: UseExternalShareProviderArgs["variant"],
  options: ShareProviderProps[]
): {
  all: ShareProviderProps[];
  nav: ShareProviderProps[];
  menu: ShareProviderProps[];
} => {
  if (variant === "dialog") {
    return { all: options, nav: [], menu: [] };
  }

  if (options.length <= MAX_NAV_PROVIDERS) {
    return {
      all: options,
      nav: options,
      menu: [],
    };
  }

  // prioritize the options that are connected in the nav
  const sortedOptions = options.sort((a, b) => {
    if (a.isConnected && b.isConnected) return 0;
    return a.isConnected ? -1 : 1;
  });

  return {
    all: options,
    nav: sortedOptions.slice(0, MAX_NAV_PROVIDERS),
    menu: sortedOptions.slice(MAX_NAV_PROVIDERS),
  };
};

/**
 * Takes a set of provider options and determines which should be shown in the
 * nav and which should be shown in the menu.
 *
 * @param variant Context in which these providers will be displayed; affects
 * how the options are segmented
 * @returns The options to show in the nav, menu, and all the options available
 */
const useExternalShareProviders = ({
  variant,
}: UseExternalShareProviderArgs) => {
  const { isProviderConnected } = useIntegrations();

  const salesforceEnabled =
    useFeatureGate("enable_salesforce").value ||
    Config.flags.enableSalesforceIntegration;

  const providers = salesforceEnabled
    ? SHARE_PROVIDERS_WITH_SALESFORCE
    : SHARE_PROVIDERS;

  const unsortedOptions: ShareProviderProps[] = providers.map((provider) => ({
    ...provider,
    isConnected: isProviderConnected({ providerId: provider.providerId }),
  }));

  return sortAndPartitionOptions(variant, unsortedOptions);
};

export default useExternalShareProviders;
